import {autorun, IAutorunOptions, isObservable, isObservableArray, isObservableMap, isObservableObject, ObservableMap} from "mobx";
import {parseStore, inject, KeyFormat} from "mobx-sync";

/**
 * Recursively converts a MobX observable tree (objects, arrays, maps) into a plain,
 * structured-cloneable JS value. Non-observable primitives and plain objects pass through unchanged.
 * Useful for postMessage / worker dispatch where structured-clone rejects observable proxies.
 */
export function MobXTreeToPlainTree<T>(value: T): T {
	if (value == null || typeof value !== "object") return value;
	if (isObservableArray(value)) {
		return (value as unknown as unknown[]).map(MobXTreeToPlainTree) as unknown as T;
	}
	if (isObservableMap(value)) {
		const out: Record<string, unknown> = {};
		(value as unknown as ObservableMap).forEach((v, k)=>{ out[k] = MobXTreeToPlainTree(v); });
		return out as unknown as T;
	}
	if (isObservableObject(value) || isObservable(value)) {
		const out: Record<string, unknown> = {};
		for (const key of Object.keys(value)) {
			out[key] = MobXTreeToPlainTree((value as Record<string, unknown>)[key]);
		}
		return out as unknown as T;
	}
	// Plain array (not observable)
	if (Array.isArray(value)) {
		return (value as unknown[]).map(MobXTreeToPlainTree) as unknown as T;
	}
	// Plain object
	const out: Record<string, unknown> = {};
	for (const key of Object.keys(value)) {
		out[key] = MobXTreeToPlainTree((value as Record<string, unknown>)[key]);
	}
	return out as unknown as T;
}

export function AutoRun(opts: {wait?: number}, func: ()=>any, autorunOpts?: IAutorunOptions) {
	if (opts.wait != null) {
		setTimeout(()=>{
			autorun(func, autorunOpts);
		}, opts.wait);
	} else {
		return autorun(func, autorunOpts);
	}
}

export function format_ObservableArray<V>(valueConstructor: ()=>V, entryFinalizer?: (result: V)=>void) {
	// Stage-3 field/accessor decorator that registers a mobx-sync `format` (deserializer/serializer) for the field,
	// mirroring mobx-sync's legacy `format(...)(target, propertyKey)` behavior (which writes to `target[KeyFormat][key]`).
	return (_value: unknown, context: ClassAccessorDecoratorContext | ClassFieldDecoratorContext)=>{
		const key = String(context.name);
		context.addInitializer(function() {
			const target = (this as any).constructor.prototype;
			inject(target, KeyFormat);
			target[KeyFormat][key] = {
				deserializer: SafeDeserializer_ObservableArray(valueConstructor, entryFinalizer),
				serializer: SafeSerializer_ObservableArray(),
			};
		});
	};
}
export function SafeDeserializer_ObservableArray<V>(valueConstructor: ()=>V, entryFinalizer?: (result: V)=>void) {
	return function(persistedValue: any[], currentValue: V[]) {
		const result = currentValue;
		result.length = 0;
		for (const value of persistedValue) {
			const freshInstance = valueConstructor();
			parseStore(freshInstance, value, false);
			if (entryFinalizer) entryFinalizer(freshInstance);
			result.push(freshInstance);
		}
		return result;
	};
}
export function SafeSerializer_ObservableArray<V>() {
	return function(arr: V[]) {
		return [...arr];
	};
}

export function format_ObservableMap<K, V>(valueConstructor: ()=>V, entryFinalizer?: (result: V)=>void) {
	// Stage-3 field/accessor decorator (mirrors mobx-sync's legacy `format(...)(target, propertyKey)`).
	return (_value: unknown, context: ClassAccessorDecoratorContext | ClassFieldDecoratorContext)=>{
		const key = String(context.name);
		context.addInitializer(function() {
			const target = (this as any).constructor.prototype;
			inject(target, KeyFormat);
			target[KeyFormat][key] = {
				deserializer: SafeDeserializer_ObservableMap(valueConstructor, entryFinalizer),
				serializer: SafeSerializer_ObservableMap(),
			};
		});
	};
}
export function SafeDeserializer_ObservableMap<K, V>(valueConstructor: ()=>V, entryFinalizer?: (result: V)=>void) {
	return function(persistedValue: Array<[K, any]>, currentValue: ObservableMap<K, V>) {
		//const result = observable.map<K, V>();
		const result = currentValue;
		for (const [key, value] of persistedValue) {
			const freshInstance = valueConstructor();
			parseStore(freshInstance, value, false);
			if (entryFinalizer) entryFinalizer(freshInstance);
			result.set(key, freshInstance);
		}
		return result;
	}
}
export function SafeSerializer_ObservableMap<K, V>() {
	return function(map: ObservableMap<K, V>) {
		return [...map.entries()];
	}
}